import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function focusables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  dismissable?: boolean;
  /** Accessible label when there's no visible title. */
  ariaLabel?: string;
}

export function Modal({ open, onClose, title, children, footer, dismissable = true, ariaLabel }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Parents usually pass an inline onClose; reading it through a ref keeps the
  // effect below from re-running (and yanking focus back to the first control)
  // every time the parent re-renders.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) {
        e.stopPropagation();
        e.preventDefault();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const items = focusables(ref.current);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = requestAnimationFrame(() => focusables(ref.current)[0]?.focus());

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus?.();
    };
  }, [open, dismissable]);

  if (!open) return null;

  return createPortal(
    <div
      className="cv-modal-backdrop"
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={ref}
        className="cv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
      >
        {(title || dismissable) && (
          <div className="cv-modal__header">
            {title ? (
              <h2 className="cv-modal__title" id={titleId}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            {dismissable && (
              <IconButton
                icon="close"
                label="Close"
                className="cv-modal__close"
                onClick={() => onCloseRef.current()}
              />
            )}
          </div>
        )}
        <div className="cv-modal__body">{children}</div>
        {footer ? <div className="cv-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
